import { Badge } from '@/components/ui/badge';
import type {
  ClaireLiveQualityReport,
  ClaireRegressionRun,
} from '@borradh-workspace/api-client/types';
import { ExternalLink } from 'lucide-react';
import { fixEffectText, fixStatusText } from './live-quality-format';

/** One line per fix: PR, what it does, status, and its effect in words. */
export function FixesSection({
  report,
  runs,
}: {
  report: ClaireLiveQualityReport;
  runs?: readonly ClaireRegressionRun[];
}) {
  const unknown = report.fixes.some((f) => f.statusSource === 'config');
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-2xl font-semibold">Fixes</h2>
        {unknown ? (
          <p className="text-muted-foreground text-sm">
            Status is read from GitHub when CLAIRE_FIXES_GITHUB_TOKEN is set on
            the API; without it, it shows as unknown.
          </p>
        ) : null}
      </div>
      <ul className="divide-y rounded-xl border">
        {report.fixes.map((fix) => (
          <li
            key={fix.pr}
            data-testid={`fix-${fix.pr}`}
            className="flex flex-col gap-1 p-3 text-sm sm:flex-row sm:items-start sm:gap-4"
          >
            <a
              href={fix.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-16 shrink-0 items-center gap-1 font-medium hover:underline"
            >
              #{fix.pr}
              <ExternalLink className="size-3" />
            </a>
            <div className="flex-1">
              <div>{fix.label}</div>
              <div className="text-muted-foreground">
                {fixEffectText(fix, report, runs)}
              </div>
            </div>
            <Badge
              className="shrink-0"
              variant={
                fix.statusSource === 'github' && fix.status === 'live'
                  ? 'default'
                  : 'outline'
              }
            >
              {fixStatusText(fix)}
            </Badge>
          </li>
        ))}
      </ul>
    </section>
  );
}
